import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import {
  Building2,
  User,
  MapPin,
  KeyRound,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useCreateCompanyRegistration } from '../../../hooks/useCompanyRegistration';

const COMPANY_TYPES = [
  { value: 'MEI', label: 'MEI — Microempreendedor Individual' },
  { value: 'ME', label: 'ME — Microempresa' },
  { value: 'EPP', label: 'EPP — Empresa de Pequeno Porte' },
  { value: 'LTDA', label: 'LTDA — Sociedade Limitada' },
  { value: 'SA', label: 'SA — Sociedade Anônima' },
  { value: 'EIRELI', label: 'EIRELI' },
  { value: 'SLU', label: 'SLU — Sociedade Limitada Unipessoal' },
];

const DOC_TYPES = [
  { value: 'RG', label: 'RG' },
  { value: 'CNH', label: 'CNH' },
];

const PRODUCT_TYPES = [
  { value: 'SERVICES', label: 'Serviços' },
  { value: 'PRODUCTS', label: 'Produtos' },
  { value: 'BOTH', label: 'Ambos' },
];

// Mask helpers
function maskCnpj(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 14);
  return d.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
}
function maskCpf(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}
function maskCep(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 8);
  return d.replace(/(\d{5})(\d)/, '$1-$2');
}
function maskPhone(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

function validateCnpj(cnpj: string): boolean {
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false;
  const calc = (digits: string, weights: number[]) =>
    weights.reduce((sum, w, i) => sum + parseInt(digits[i]) * w, 0);
  const w1 = [5,4,3,2,9,8,7,6,5,4,3,2];
  const w2 = [6,5,4,3,2,9,8,7,6,5,4,3,2];
  const r1 = calc(d, w1) % 11;
  const d1 = r1 < 2 ? 0 : 11 - r1;
  if (parseInt(d[12]) !== d1) return false;
  const r2 = calc(d, w2) % 11;
  const d2 = r2 < 2 ? 0 : 11 - r2;
  return parseInt(d[13]) === d2;
}

function validateCpf(cpf: string): boolean {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(d[i]) * (10 - i);
  let r = (sum * 10) % 11; if (r === 10) r = 0;
  if (parseInt(d[9]) !== r) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(d[i]) * (11 - i);
  r = (sum * 10) % 11; if (r === 10) r = 0;
  return parseInt(d[10]) === r;
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validatePassword(pw: string): boolean {
  return pw.length >= 8 && /[A-Z]/.test(pw) && /\d/.test(pw) && /[!@#$%^&*]/.test(pw);
}

interface FormErrors {
  [key: string]: string;
}

export function CompanyRegister() {
  const navigate = useNavigate();
  const createMutation = useCreateCompanyRegistration();
  const [showOptional, setShowOptional] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const [form, setForm] = useState({
    companyName: '', companyCnpj: '', companyType: 'LTDA', tradeName: '',
    companyEmail: '', companyPhone: '', companyWebsite: '', companyDescription: '',
    representativeName: '', representativeCpf: '', representativeDocType: 'RG',
    representativeBirthDate: '', representativePhone: '', representativeMotherName: '',
    addressZipCode: '', addressStreet: '', addressNumber: '', addressNeighborhood: '',
    addressCity: '', addressState: '', addressComplement: '',
    accountEmail: '', accountPassword: '',
    // Optional
    sellerName: '', sacPhone: '', averageMonthlyTurnover: '', averageTicket: '',
    typeProduct: '', productsSold: '', companyObjectives: '', companyAge: '',
  });

  const update = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  // ViaCEP auto-fill
  useEffect(() => {
    const cep = form.addressZipCode.replace(/\D/g, '');
    if (cep.length === 8) {
      fetch(`https://viacep.com.br/ws/${cep}/json/`)
        .then(r => r.json())
        .then(data => {
          if (!data.erro) {
            setForm(prev => ({
              ...prev,
              addressStreet: data.logradouro || prev.addressStreet,
              addressNeighborhood: data.bairro || prev.addressNeighborhood,
              addressCity: data.localidade || prev.addressCity,
              addressState: data.uf || prev.addressState,
            }));
          }
        })
        .catch(() => {});
    }
  }, [form.addressZipCode]);

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (form.companyName.length < 3) e.companyName = 'Nome deve ter no mínimo 3 caracteres';
    if (!validateCnpj(form.companyCnpj)) e.companyCnpj = 'CNPJ inválido';
    if (!form.tradeName) e.tradeName = 'Nome fantasia é obrigatório';
    if (!validateEmail(form.companyEmail)) e.companyEmail = 'Email inválido';
    if (!form.companyPhone.replace(/\D/g, '')) e.companyPhone = 'Telefone é obrigatório';
    if (!form.companyDescription) e.companyDescription = 'Descrição é obrigatória';
    if (form.representativeName.length < 3) e.representativeName = 'Nome deve ter no mínimo 3 caracteres';
    if (!validateCpf(form.representativeCpf)) e.representativeCpf = 'CPF inválido';
    if (!form.representativeBirthDate) e.representativeBirthDate = 'Data de nascimento é obrigatória';
    else {
      const age = (Date.now() - new Date(form.representativeBirthDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
      if (age < 18) e.representativeBirthDate = 'Representante deve ter no mínimo 18 anos';
    }
    if (!form.representativePhone.replace(/\D/g, '')) e.representativePhone = 'Telefone é obrigatório';
    if (!form.representativeMotherName) e.representativeMotherName = 'Nome da mãe é obrigatório';
    if (form.addressZipCode.replace(/\D/g, '').length !== 8) e.addressZipCode = 'CEP inválido';
    if (!form.addressStreet) e.addressStreet = 'Logradouro é obrigatório';
    if (!form.addressNumber) e.addressNumber = 'Número é obrigatório';
    if (!form.addressNeighborhood) e.addressNeighborhood = 'Bairro é obrigatório';
    if (!form.addressCity) e.addressCity = 'Cidade é obrigatória';
    if (form.addressState.length !== 2) e.addressState = 'UF deve ter 2 letras';
    if (!validateEmail(form.accountEmail)) e.accountEmail = 'Email inválido';
    if (!validatePassword(form.accountPassword)) e.accountPassword = 'Mínimo 8 caracteres, 1 maiúscula, 1 número, 1 especial (!@#$%^&*)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = {
      ...form,
      companyCnpj: form.companyCnpj.replace(/\D/g, ''),
      representativeCpf: form.representativeCpf.replace(/\D/g, ''),
      addressZipCode: form.addressZipCode.replace(/\D/g, ''),
      companyPhone: form.companyPhone.replace(/\D/g, ''),
      representativePhone: form.representativePhone.replace(/\D/g, ''),
    };
    createMutation.mutate(payload, {
      onSuccess: () => navigate('/banking/company/documents'),
    });
  };

  const fieldClass = (field: string) =>
    errors[field] ? 'border-red-500 focus:border-red-500' : '';

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white">Cadastro de Empresa PJ</h1>
        <p className="text-surface-400 mt-1">
          Preencha os dados da empresa para abrir sua conta empresarial.
        </p>
      </div>

      {createMutation.isError && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-medium">Erro ao salvar cadastro</p>
            <p className="text-red-300 text-sm">
              {(createMutation.error as any)?.response?.data?.message || 'Tente novamente.'}
            </p>
          </div>
        </div>
      )}

      {/* Dados da Empresa */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Building2 size={20} className="text-primary-400" />
            Dados da Empresa
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input label="Nome da Empresa *" placeholder="Empresa LTDA" value={form.companyName}
                onChange={e => update('companyName', e.target.value)} className={fieldClass('companyName')} />
              {errors.companyName && <p className="text-red-400 text-xs mt-1">{errors.companyName}</p>}
            </div>
            <div>
              <Input label="CNPJ *" placeholder="00.000.000/0000-00" value={form.companyCnpj}
                onChange={e => update('companyCnpj', maskCnpj(e.target.value))} className={fieldClass('companyCnpj')} />
              {errors.companyCnpj && <p className="text-red-400 text-xs mt-1">{errors.companyCnpj}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">Tipo de Empresa *</label>
              <select className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white"
                value={form.companyType} onChange={e => update('companyType', e.target.value)}>
                {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Input label="Nome Fantasia *" placeholder="Nome Fantasia" value={form.tradeName}
                onChange={e => update('tradeName', e.target.value)} className={fieldClass('tradeName')} />
              {errors.tradeName && <p className="text-red-400 text-xs mt-1">{errors.tradeName}</p>}
            </div>
            <div>
              <Input label="Email da Empresa *" placeholder="contato@empresa.com.br" type="email"
                value={form.companyEmail} onChange={e => update('companyEmail', e.target.value)} className={fieldClass('companyEmail')} />
              {errors.companyEmail && <p className="text-red-400 text-xs mt-1">{errors.companyEmail}</p>}
            </div>
            <div>
              <Input label="Telefone *" placeholder="(11) 99999-9999" value={form.companyPhone}
                onChange={e => update('companyPhone', maskPhone(e.target.value))} className={fieldClass('companyPhone')} />
              {errors.companyPhone && <p className="text-red-400 text-xs mt-1">{errors.companyPhone}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">Descrição do Negócio *</label>
            <textarea className={`w-full bg-surface-800 border rounded-lg px-3 py-2 text-white min-h-[80px] ${errors.companyDescription ? 'border-red-500' : 'border-surface-700'}`}
              placeholder="Descreva o negócio da empresa..." value={form.companyDescription}
              onChange={e => update('companyDescription', e.target.value)} />
            {errors.companyDescription && <p className="text-red-400 text-xs mt-1">{errors.companyDescription}</p>}
          </div>
        </div>
      </Card>

      {/* Representante Legal */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <User size={20} className="text-primary-400" />
            Representante Legal
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input label="Nome Completo *" placeholder="João da Silva" value={form.representativeName}
                onChange={e => update('representativeName', e.target.value)} className={fieldClass('representativeName')} />
              {errors.representativeName && <p className="text-red-400 text-xs mt-1">{errors.representativeName}</p>}
            </div>
            <div>
              <Input label="CPF *" placeholder="000.000.000-00" value={form.representativeCpf}
                onChange={e => update('representativeCpf', maskCpf(e.target.value))} className={fieldClass('representativeCpf')} />
              {errors.representativeCpf && <p className="text-red-400 text-xs mt-1">{errors.representativeCpf}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">Tipo de Documento *</label>
              <select className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white"
                value={form.representativeDocType} onChange={e => update('representativeDocType', e.target.value)}>
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Input label="Data de Nascimento *" type="date" value={form.representativeBirthDate}
                onChange={e => update('representativeBirthDate', e.target.value)} className={fieldClass('representativeBirthDate')} />
              {errors.representativeBirthDate && <p className="text-red-400 text-xs mt-1">{errors.representativeBirthDate}</p>}
            </div>
            <div>
              <Input label="Telefone *" placeholder="(11) 99999-9999" value={form.representativePhone}
                onChange={e => update('representativePhone', maskPhone(e.target.value))} className={fieldClass('representativePhone')} />
              {errors.representativePhone && <p className="text-red-400 text-xs mt-1">{errors.representativePhone}</p>}
            </div>
            <div>
              <Input label="Nome da Mãe *" placeholder="Maria da Silva" value={form.representativeMotherName}
                onChange={e => update('representativeMotherName', e.target.value)} className={fieldClass('representativeMotherName')} />
              {errors.representativeMotherName && <p className="text-red-400 text-xs mt-1">{errors.representativeMotherName}</p>}
            </div>
          </div>
        </div>
      </Card>

      {/* Endereço */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MapPin size={20} className="text-primary-400" />
            Endereço
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input label="CEP *" placeholder="00000-000" value={form.addressZipCode}
                onChange={e => update('addressZipCode', maskCep(e.target.value))} className={fieldClass('addressZipCode')} />
              {errors.addressZipCode && <p className="text-red-400 text-xs mt-1">{errors.addressZipCode}</p>}
            </div>
            <div>
              <Input label="Logradouro *" placeholder="Rua Exemplo" value={form.addressStreet}
                onChange={e => update('addressStreet', e.target.value)} className={fieldClass('addressStreet')} />
              {errors.addressStreet && <p className="text-red-400 text-xs mt-1">{errors.addressStreet}</p>}
            </div>
            <div>
              <Input label="Número *" placeholder="123" value={form.addressNumber}
                onChange={e => update('addressNumber', e.target.value)} className={fieldClass('addressNumber')} />
              {errors.addressNumber && <p className="text-red-400 text-xs mt-1">{errors.addressNumber}</p>}
            </div>
            <div>
              <Input label="Bairro *" placeholder="Centro" value={form.addressNeighborhood}
                onChange={e => update('addressNeighborhood', e.target.value)} className={fieldClass('addressNeighborhood')} />
              {errors.addressNeighborhood && <p className="text-red-400 text-xs mt-1">{errors.addressNeighborhood}</p>}
            </div>
            <div>
              <Input label="Cidade *" placeholder="São Paulo" value={form.addressCity}
                onChange={e => update('addressCity', e.target.value)} className={fieldClass('addressCity')} />
              {errors.addressCity && <p className="text-red-400 text-xs mt-1">{errors.addressCity}</p>}
            </div>
            <div>
              <Input label="Estado (UF) *" placeholder="SP" maxLength={2} value={form.addressState}
                onChange={e => update('addressState', e.target.value.toUpperCase())} className={fieldClass('addressState')} />
              {errors.addressState && <p className="text-red-400 text-xs mt-1">{errors.addressState}</p>}
            </div>
            <div className="md:col-span-2">
              <Input label="Complemento" placeholder="Sala 101" value={form.addressComplement}
                onChange={e => update('addressComplement', e.target.value)} />
            </div>
          </div>
        </div>
      </Card>

      {/* Credenciais */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <KeyRound size={20} className="text-primary-400" />
            Credenciais de Acesso
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input label="Email de Acesso *" placeholder="conta@empresa.com.br" type="email"
                value={form.accountEmail} onChange={e => update('accountEmail', e.target.value)} className={fieldClass('accountEmail')} />
              {errors.accountEmail && <p className="text-red-400 text-xs mt-1">{errors.accountEmail}</p>}
            </div>
            <div>
              <Input label="Senha *" placeholder="Mínimo 8 caracteres" type="password"
                value={form.accountPassword} onChange={e => update('accountPassword', e.target.value)} className={fieldClass('accountPassword')} />
              {errors.accountPassword && <p className="text-red-400 text-xs mt-1">{errors.accountPassword}</p>}
            </div>
          </div>
        </div>
      </Card>

      {/* Campos Opcionais */}
      <Card>
        <div className="p-6">
          <button onClick={() => setShowOptional(!showOptional)}
            className="flex items-center gap-2 text-surface-300 hover:text-white transition-colors w-full">
            {showOptional ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            <span className="font-medium">Campos Opcionais</span>
            <span className="text-xs text-surface-500">(website, faturamento, etc.)</span>
          </button>
          {showOptional && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input label="Website" placeholder="https://empresa.com.br" value={form.companyWebsite}
                onChange={e => update('companyWebsite', e.target.value)} />
              <Input label="Razão Social" placeholder="Razão Social" value={form.sellerName}
                onChange={e => update('sellerName', e.target.value)} />
              <Input label="Telefone SAC" placeholder="(11) 99999-9999" value={form.sacPhone}
                onChange={e => update('sacPhone', maskPhone(e.target.value))} />
              <Input label="Faturamento Mensal Médio" placeholder="R$ 50.000" value={form.averageMonthlyTurnover}
                onChange={e => update('averageMonthlyTurnover', e.target.value)} />
              <Input label="Ticket Médio" placeholder="R$ 500" value={form.averageTicket}
                onChange={e => update('averageTicket', e.target.value)} />
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">Tipo de Produto</label>
                <select className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white"
                  value={form.typeProduct} onChange={e => update('typeProduct', e.target.value)}>
                  <option value="">Selecione...</option>
                  {PRODUCT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <Input label="Produtos Vendidos" placeholder="Descreva os produtos" value={form.productsSold}
                onChange={e => update('productsSold', e.target.value)} />
              <Input label="Objetivos da Empresa" placeholder="Objetivos" value={form.companyObjectives}
                onChange={e => update('companyObjectives', e.target.value)} />
              <Input label="Tempo de Empresa" placeholder="Ex: 5 anos" value={form.companyAge}
                onChange={e => update('companyAge', e.target.value)} />
            </div>
          )}
        </div>
      </Card>

      {/* Submit */}
      <div className="flex justify-end pb-8">
        <Button variant="primary" size="lg" onClick={handleSubmit} disabled={createMutation.isPending}>
          {createMutation.isPending ? (
            <><Loader2 size={20} className="mr-2 animate-spin" />Salvando...</>
          ) : (
            <><Building2 size={20} className="mr-2" />Salvar e Continuar</>
          )}
        </Button>
      </div>
    </div>
  );
}
